/**
 * Admin edit of a submitted registration form (ADB-169). Same fields and rules as the personal
 * edit link, but with staff permissions: no expiry, any registration status, signature optional.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { eventParticipantsKeys } from '../hooks/useEventParticipants';
import {
  adminGetRegistrationEdit,
  adminUpdateRegistration,
  getRegistrationDocumentUrl,
  uploadRegistrationDocument,
} from '../public/publicRegistrationRepository';
import { RegistrationEditForm } from '../public/components/RegistrationEditForm';
import type { DocumentKind, RegistrationEditPayload } from '../public/types';

interface EditRegistrationDialogProps {
  eventId: string;
  participant: ParticipantWithDetails | null;
  onOpenChange: (open: boolean) => void;
}

export function EditRegistrationDialog({ eventId, participant, onOpenChange }: EditRegistrationDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const participantId = participant?.id;
  const { data: view, isLoading, error: loadError } = useQuery({
    queryKey: ['registration_edit_admin', participantId],
    queryFn: () => adminGetRegistrationEdit(participantId as string),
    enabled: !!participantId,
    retry: false,
    // Always the current data: this form saves what it shows
    gcTime: 0,
  });

  const close = () => {
    setError(null);
    onOpenChange(false);
  };

  const save = async (payload: RegistrationEditPayload) => {
    if (!participantId) return;
    setSaving(true);
    setError(null);
    try {
      await adminUpdateRegistration(participantId, payload);
      await queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: ['public_registration'] });
      toast({ title: 'Formulario actualizado' });
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const name = participant?.minister?.full_name ?? participant?.guest?.full_name ?? 'Participante';

  return (
    <Dialog open={!!participant} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar formulario — {name}</DialogTitle>
          <DialogDescription>
            Cambia los datos del formulario de inscripción. El CI y los datos que vienen del registro ministerial no se
            modifican aquí, y el precio no se recalcula.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Cargando…
          </p>
        )}
        {loadError && (
          <Alert variant="destructive">
            <AlertDescription>{loadError instanceof Error ? loadError.message : 'No se pudo cargar el formulario'}</AlertDescription>
          </Alert>
        )}
        {view && participant && (
          <RegistrationEditForm
            key={participant.id}
            view={view}
            mode="admin"
            upload={(kind: DocumentKind, file: Blob) => uploadRegistrationDocument(view.event_id, kind, file)}
            loadDocument={async (kind: DocumentKind) => {
              const key = participant.documents?.[kind];
              if (!key) return null;
              return { url: await getRegistrationDocumentUrl(key), isPdf: key.toLowerCase().endsWith('.pdf') };
            }}
            cacheKey={participant.id}
            saving={saving}
            error={error}
            onSave={save}
            onCancel={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
